/**
 * The cockpit-facing edge of the frame loop: working out what the painted
 * panel should show this frame, pushing it out only on change, and keeping the
 * painted gun breeches registered against the viewport. A sibling of
 * `flight-scene.ts` on the same pattern as `mission-report.ts` and
 * `scene-propellers.ts` — the loop's cockpit concerns in one place, and the
 * scene file under its size gate.
 */

import type { FlightInputs, SimState } from "@red-opus/engine";
import { controlSurfaceStateFrom, muzzleFlashVisible } from "./cockpit-frame.ts";
import { advanceCockpitHits, type CockpitHitState } from "./cockpit-hits.ts";
import { CAMERA_FOV_DEG } from "./flight-view.ts";
import type { ViewMetrics } from "../three/view-metrics.ts";
import type { GunGeometry, GunMuzzles } from "./gun-muzzles.ts";
import type { GlanceDirection } from "./keys.ts";
import type { CockpitViewState, FlightSceneCallbacks } from "./flight-scene-api.ts";

export { NO_COCKPIT_HITS, type CockpitHitState } from "./cockpit-hits.ts";

/** The slice of `flight-scene.ts`'s `SceneState` the cockpit reporting reads and latches. */
export interface CockpitReportState {
  cockpitView: CockpitViewState;
  /** Accumulated bullet holes, advanced here and never cleared (`cockpit-hits.ts`). */
  cockpitHits: CockpitHitState;
  readonly muzzles: GunMuzzles;
  readonly container: HTMLDivElement;
}

function sameSurfaces(a: CockpitViewState["surfaces"], b: CockpitViewState["surfaces"]): boolean {
  return a.aileron === b.aileron && a.elevator === b.elevator && a.rudder === b.rudder;
}

/** Everything this frame's painted cockpit depends on, straight off the loop. */
export interface CockpitFrameInput {
  readonly glance: GlanceDirection;
  /**
   * Whether a right-mouse free look is live (`free-look.ts`). Carried straight
   * through to the panel rather than folded into `glance`: the *camera* already
   * has the angle, and this is the one bit the painted panel needs — it cannot
   * follow a continuous view, so it stands down while one is held, exactly as it
   * does for the `up` glance the art has no picture for.
   */
  readonly freeLook: boolean;
  /** The same stick the flight core consumed this tick — never a second keyboard read. */
  readonly inputs: FlightInputs;
  readonly simState: SimState;
  /**
   * Rounds from *guns* that have struck the player, cumulative. Archie is
   * excluded upstream: `damage.c` skips the cockpit overlays for its two flak
   * damage kinds, so a shelling never marks the panel.
   */
  readonly gunHitsTaken: number;
}

/**
 * Advances the cockpit's accumulated hit marks and pushes the panel's
 * glance/flash/surface/marks state out — but only when it actually changed.
 *
 * The marks are advanced here rather than in the scene because the choice of
 * which overlay a round marks depends on the glance being held this frame
 * (`cockpit-hits.ts`), which is the one piece of state this module already
 * owns the freshest copy of.
 */
export function reportCockpitView(
  state: CockpitReportState,
  callbacks: FlightSceneCallbacks,
  input: CockpitFrameInput,
): void {
  const { glance, inputs, simState } = input;
  state.cockpitHits = advanceCockpitHits(state.cockpitHits, {
    gunHitsTaken: input.gunHitsTaken,
    glance,
    damageable: simState.config.realism.aircraftMayBeDamaged,
  });
  const next: CockpitViewState = {
    glance,
    freeLook: input.freeLook,
    gunFlash: muzzleFlashVisible(simState.guns.burstRounds, simState.guns.jammed, simState.config.spec.guns.roundsPerSecond),
    surfaces: controlSurfaceStateFrom(inputs),
    hitMarks: state.cockpitHits.marks,
  };
  if (
    next.glance === state.cockpitView.glance &&
    next.freeLook === state.cockpitView.freeLook &&
    next.gunFlash === state.cockpitView.gunFlash &&
    next.hitMarks === state.cockpitView.hitMarks &&
    sameSurfaces(next.surfaces, state.cockpitView.surfaces)
  ) {
    return;
  }
  state.cockpitView = next;
  callbacks.onCockpitView?.(next);
}

/** The gun geometry the sim fired with, so the drawn muzzles can be expressed as a difference from it. */
export function gunGeometryOf(simState: SimState): GunGeometry {
  return { gun: simState.config.spec.guns, tunables: simState.config.tunables };
}

/**
 * Re-registers the painted gun breeches against the viewport. The cockpit
 * panel is presented at an integer scale anchored bottom-centre rather than
 * stretched (`panel-layout.ts`), so a resize moves the painted muzzle flash to
 * a different place in the camera's frustum and the registration has to be
 * recomputed — see `gun-muzzles.ts`.
 */
export function updateMuzzleViewport(state: CockpitReportState, metrics: ViewMetrics): void {
  state.muzzles.setViewport({
    widthPx: metrics.widthCssPx,
    heightPx: metrics.heightCssPx,
    fovDeg: CAMERA_FOV_DEG,
  });
}
